import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap } from 'rxjs';
import { environments } from '../../../environments/environment';
import { CsrfBootstrapService } from '../services/csrf-bootstrap.service';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function isApiRequest(url: string): boolean {
  return url.startsWith(environments.apiUrl) || url.includes('/api/');
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function attachCsrfHeader(req: Parameters<HttpInterceptorFn>[0], token: string) {
  return req.clone({
    setHeaders: {
      'X-XSRF-TOKEN': token
    }
  });
}

export const csrfInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url) || !MUTATING_METHODS.has(req.method)) {
    return next(req);
  }

  if (req.headers.has('Authorization')) {
    return next(req);
  }

  const csrfBootstrap = inject(CsrfBootstrapService);
  const existingToken = csrfBootstrap.getToken() ?? readCookie('XSRF-TOKEN');

  if (existingToken) {
    return next(attachCsrfHeader(req, existingToken));
  }

  return from(csrfBootstrap.ensurePrimed()).pipe(
    switchMap(() => {
      const token = csrfBootstrap.getToken() ?? readCookie('XSRF-TOKEN');
      return next(token ? attachCsrfHeader(req, token) : req);
    })
  );
};
